// V2 Investment Progress - Split into PENDING and POST-PENDING phases

/**
 * Investment step type - represents a stage in the investment workflow
 */
export type InvestmentStep =
  | "PROFILE"
  | "SUBSCRIPTION"
  | "PAYMENT"
  | "ADDRESS"
  | "SIGNERS"
  | "KYC"
  | "AGREEMENT"
  | "ACCREDITATION"
  | "DILIGENCE"
  | "COUNTERSIGN"
  | "CLOSING";

// PENDING phase steps - shown when investment.status === "PENDING"
export const pendingStepsAccreditation: readonly InvestmentStep[] = [
  "PROFILE",
  "SUBSCRIPTION",
  "PAYMENT",
  "ADDRESS",
  "SIGNERS",
  "KYC",
  "AGREEMENT",
  "ACCREDITATION",
] as const;

export const pendingStepsNoAccreditation: readonly InvestmentStep[] = [
  "PROFILE",
  "SUBSCRIPTION",
  "PAYMENT",
  "ADDRESS",
  "SIGNERS",
  "KYC",
  "AGREEMENT",
] as const;

// POST-PENDING phase steps - shown when investment moves past PENDING status
export const postPendingStepsAccreditation: readonly InvestmentStep[] = [
  "DILIGENCE",
  "COUNTERSIGN",
  "CLOSING",
] as const;

export const postPendingStepsNoAccreditation: readonly InvestmentStep[] = ["COUNTERSIGN", "CLOSING"] as const;

/**
 * Step copy configuration - text and headings for each step
 */
interface StepCopyConfig {
  text: (isRegA?: boolean) => string;
  heading: (isRegA?: boolean) => string;
}

export const investmentStepsCopy: Record<InvestmentStep, StepCopyConfig> = {
  PROFILE: {
    text: () =>
      "The investment has been started. You must provide the necessary information to generate the agreements.",
    heading: () => "Profile",
  },
  SUBSCRIPTION: {
    text: () => "You have not yet indicated the amount you want to invest.",
    heading: () => "Subscription",
  },
  PAYMENT: {
    text: () => "You have not yet selected a payment method for your investment.",
    heading: () => "Payment",
  },
  ADDRESS: {
    text: () => "You must provide your address for the subscription agreement.",
    heading: () => "Address",
  },
  SIGNERS: {
    text: () => "You must indicate additional information and add signers and UBOs, if applicable.",
    heading: () => "Signers",
  },
  KYC: {
    text: () => "You must verify that your information is correct and continue with your investment.",
    heading: () => "Investment Review",
  },
  AGREEMENT: {
    text: () => "You must sign the subscription agreement.",
    heading: () => "Agreement",
  },
  ACCREDITATION: {
    text: (isRegA) =>
      isRegA
        ? "You must complete the additional information form."
        : "You must verify your accreditation status by uploading accreditation documents.",
    heading: (isRegA) => (isRegA ? "Additional Info" : "Accreditation"),
  },
  DILIGENCE: {
    text: () => "Your investment has been submitted and is awaiting review by the company.",
    heading: () => "Processing: Review",
  },
  COUNTERSIGN: {
    text: () => "Your investment has been reviewed, and your investment can now be accepted by the company.",
    heading: () => "Processing: Acceptance",
  },
  CLOSING: {
    text: () => "Your investment has been accepted, and is in the queue for the next closing.",
    heading: () => "Processing: Closing",
  },
};
